import React, { useState } from 'react';
import Layout from '../../components/layout/Layout';
import Hero from '../../components/sections/Hero';
import Section from '../../components/layout/Section';
import Card from '../../components/ui/Card';
import CallToAction from '../../components/sections/CallToAction';
import { freeResources } from '../../data/mockData';

const ResourcesPage: React.FC = () => {
  const [resourceType, setResourceType] = useState('all');
  
  // Filter resources based on type
  const filteredResources = resourceType === 'all' 
    ? freeResources
    : freeResources.filter(resource => resource.type && resource.type.toLowerCase().includes(resourceType.toLowerCase()));

  return (
    <Layout>
      <Hero
        title="AI Resources Library"
        subtitle="Free tools, guides, templates, and educational materials to help you navigate the world of AI implementation."
        ctaText="Explore Resources"
        ctaLink="#resources"
        backgroundType="gradient"
      />

      {/* Resource Categories */}
      <Section className="bg-white py-16" id="resources">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Resource Categories</h2>
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <button 
              onClick={() => setResourceType('all')}
              className={`px-6 py-3 rounded-full ${
                resourceType === 'all' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Resources
            </button>
            <button 
              onClick={() => setResourceType('guide')}
              className={`px-6 py-3 rounded-full ${
                resourceType === 'guide' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Guides & Ebooks
            </button>
            <button 
              onClick={() => setResourceType('tool')}
              className={`px-6 py-3 rounded-full ${
                resourceType === 'tool' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Interactive Tools
            </button>
            <button 
              onClick={() => setResourceType('template')}
              className={`px-6 py-3 rounded-full ${
                resourceType === 'template' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Templates
            </button>
            <button 
              onClick={() => setResourceType('calculator')}
              className={`px-6 py-3 rounded-full ${
                resourceType === 'calculator' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Calculators
            </button>
          </div>

          {/* Resources Grid */}
          <div className="grid md:grid-cols-3 gap-8">
            {filteredResources.map(resource => (
              <div key={resource.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="h-48 bg-gray-200">
                  {/* Placeholder for resource image */}
                  <div className="w-full h-full flex items-center justify-center text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                </div>
                <div className="p-6">
                  <div className="inline-block bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-3">
                    {resource.type}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{resource.title}</h3>
                  <p className="text-gray-700 mb-4">{resource.description}</p>
                  <a 
                    href={resource.url} 
                    className="inline-block bg-primary hover:bg-primary-dark text-white font-bold py-2 px-6 rounded transition-colors duration-200"
                  >
                    Download
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Add more resources coming soon card */}
          <div className="mt-12 bg-gray-50 rounded-lg p-8 text-center">
            <h3 className="text-2xl font-bold mb-4">More Resources Coming Soon</h3>
            <p className="text-gray-700 mb-6">
              We're constantly adding new resources to help you on your AI journey. 
              Subscribe to our newsletter to be notified when new resources are available.
            </p>
            <div className="flex flex-col md:flex-row gap-2 justify-center max-w-xl mx-auto">
              <input 
                type="email" 
                placeholder="Your email address" 
                className="flex-grow p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-6 rounded transition-colors duration-200">
                Notify Me
              </button>
            </div>
          </div>
        </div>
      </Section>

      {/* Featured Resources */}
      <Section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Popular Downloads</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">AI Implementation Checklist</h3>
              <p className="text-gray-600 text-center text-sm mb-4">1,200+ downloads</p>
              <a href="#" className="block text-center text-primary font-medium hover:underline">Download (PDF)</a>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">AI ROI Calculator</h3>
              <p className="text-gray-600 text-center text-sm mb-4">950+ downloads</p>
              <a href="#" className="block text-center text-primary font-medium hover:underline">Access Tool</a>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">AI Readiness Assessment</h3>
              <p className="text-gray-600 text-center text-sm mb-4">820+ downloads</p>
              <a href="#" className="block text-center text-primary font-medium hover:underline">Take Assessment</a>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary bg-opacity-10 rounded-full flex items-center justify-center text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-center mb-2">AI Use Case Template</h3>
              <p className="text-gray-600 text-center text-sm mb-4">750+ downloads</p>
              <a href="#" className="block text-center text-primary font-medium hover:underline">Download (DOCX)</a>
            </div>
          </div>
        </div>
      </Section>

      {/* Video Tutorial Section */}
      <Section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Video Tutorials</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="relative h-48 bg-gray-200">
                {/* Placeholder for video thumbnail */}
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white bg-opacity-80 rounded-full p-3 shadow-lg cursor-pointer hover:bg-opacity-100 transition duration-200">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Getting Started with AI Implementation</h3>
                <p className="text-gray-600 text-sm mb-2">15:32 • Beginner</p>
                <p className="text-gray-700 mb-4">A comprehensive introduction to implementing AI in your business, covering key concepts, tools, and first steps.</p>
                <a href="#" className="text-primary font-medium hover:underline">Watch Tutorial</a>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="relative h-48 bg-gray-200">
                {/* Placeholder for video thumbnail */}
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white bg-opacity-80 rounded-full p-3 shadow-lg cursor-pointer hover:bg-opacity-100 transition duration-200">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Building Your First AI Workflow</h3>
                <p className="text-gray-600 text-sm mb-2">22:47 • Intermediate</p>
                <p className="text-gray-700 mb-4">Step-by-step instructions for creating your first automated AI workflow to streamline business processes.</p>
                <a href="#" className="text-primary font-medium hover:underline">Watch Tutorial</a>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="relative h-48 bg-gray-200">
                {/* Placeholder for video thumbnail */}
                <div className="w-full h-full flex items-center justify-center text-gray-500">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white bg-opacity-80 rounded-full p-3 shadow-lg cursor-pointer hover:bg-opacity-100 transition duration-200">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">Measuring AI Implementation Success</h3>
                <p className="text-gray-600 text-sm mb-2">18:05 • Advanced</p>
                <p className="text-gray-700 mb-4">Learn how to define, track, and analyze key performance indicators for your AI implementations.</p>
                <a href="#" className="text-primary font-medium hover:underline">Watch Tutorial</a>
              </div>
            </div>
          </div>
          <div className="text-center mt-8">
            <a href="#" className="inline-block border border-primary text-primary hover:bg-primary hover:text-white font-bold py-2 px-6 rounded transition-colors duration-200">
              View All Tutorials
            </a>
          </div>
        </div>
      </Section>

      {/* Call to Action */}
      <CallToAction
        title="Need a Custom Resource?"
        description="Can't find what you're looking for? Contact us to request a custom resource tailored to your specific needs."
        ctaText="Contact Us"
        ctaLink="/contact"
      />
    </Layout>
  );
};

export default ResourcesPage;